import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  Alert,
  AlertType,
  AlertConfig,
  PersistenceLevel,
} from '@surface-elements/shared/alert-system/domain';
import * as AlertActions from './alert.actions';
import * as AlertSelectors from './alert.selectors';

@Injectable({
  providedIn: 'root',
})
export class AlertFacade {
  private readonly store = inject(Store);

  allAlerts$: Observable<Alert[]> = this.store.select(
    AlertSelectors.selectAllAlerts,
  );
  config$: Observable<AlertConfig> = this.store.select(
    AlertSelectors.selectAlertConfig,
  );
  loaded$: Observable<boolean> = this.store.select(
    AlertSelectors.selectAlertsLoaded,
  );
  error$: Observable<string | null> = this.store.select(
    AlertSelectors.selectAlertError,
  );

  success(title: string, message: string, options?: Partial<Alert>): void {
    this.addAlert({
      type: AlertType.SUCCESS,
      title,
      message,
      dismissible: true,
      persistence: options?.persistence ?? PersistenceLevel.NONE,
      ...options,
    });
  }

  info(title: string, message: string, options?: Partial<Alert>): void {
    this.addAlert({
      type: AlertType.INFO,
      title,
      message,
      dismissible: true,
      persistence: options?.persistence ?? PersistenceLevel.NONE,
      ...options,
    });
  }

  warning(title: string, message: string, options?: Partial<Alert>): void {
    this.addAlert({
      type: AlertType.WARNING,
      title,
      message,
      dismissible: true,
      persistence: options?.persistence ?? PersistenceLevel.NONE,
      ...options,
    });
  }

  error(title: string, message: string, options?: Partial<Alert>): void {
    this.addAlert({
      type: AlertType.ERROR,
      title,
      message,
      dismissible: true,
      persistence: options?.persistence ?? PersistenceLevel.NONE,
      ...options,
    });
  }

  addAlert(alert: Omit<Alert, 'id' | 'timestamp'>): void {
    this.store.dispatch(AlertActions.addAlert({ alert }));
  }

  removeAlert(id: string): void {
    this.store.dispatch(AlertActions.removeAlert({ id }));
  }

  clearAlerts(type?: AlertType): void {
    this.store.dispatch(AlertActions.clearAlerts({ type }));
  }

  updateConfig(config: Partial<AlertConfig>): void {
    this.store.dispatch(AlertActions.updateConfig({ config }));
  }

  loadPersistedAlerts(): void {
    this.store.dispatch(AlertActions.loadPersistedAlerts());
  }

  getAlertsByType(type: AlertType): Observable<Alert[]> {
    return this.store.select(AlertSelectors.selectAlertsByType(type));
  }

  getAlertById(id: string): Observable<Alert | undefined> {
    return this.store.select(AlertSelectors.selectAlertById(id));
  }

  getDismissibleAlerts(): Observable<Alert[]> {
    return this.store.select(AlertSelectors.selectDismissibleAlerts);
  }
}
